import { useCallback, useEffect, useRef } from 'react';

import { showSuccessMessage } from '../../../helpers/utils/ui';
import { DashboardComponent } from '../types';

const MAX_HISTORY_SIZE = 50;

interface UseDashboardHistoryOptions {
  /** Включить/выключить горячие клавиши */
  enabled?: boolean;
  /** Callback после undo — получает восстановлённые и удалённые blockId */
  onRestore?: (restoredBlockIds: string[], removedBlockIds: string[]) => void;
}

interface UseDashboardHistoryReturn {
  /** Сохранить текущее состояние перед мутацией */
  pushSnapshot: () => void;
  /** Отменить последнее действие */
  undo: () => void;
  /** Повторить отменённое действие */
  redo: () => void;
  /** Можно ли отменить */
  canUndo: boolean;
  /** Можно ли повторить */
  canRedo: boolean;
}

/**
 * Хук для управления историей состояния дашборда (undo/redo).
 *
 * Хранит снимки массива dashboardComponents перед каждой мутацией.
 * Поддерживает:
 * - Ctrl+Z / Cmd+Z — отмена
 * - Ctrl+Shift+Z / Cmd+Shift+Z / Ctrl+Y — повтор
 *
 * @param dashboardComponents — текущий массив блоков
 * @param setDashboardComponents — сеттер из useDashboardLayout
 * @param options — опции
 */
export const useDashboardHistory = (
  dashboardComponents: DashboardComponent[],
  setDashboardComponents: React.Dispatch<React.SetStateAction<DashboardComponent[]>>,
  options: UseDashboardHistoryOptions = {}
): UseDashboardHistoryReturn => {
  const { enabled = true, onRestore } = options;

  // Стеки undo/redo хранятся в ref (не влияют на рендер)
  const historyRef = useRef<DashboardComponent[][]>([]);
  const futureRef = useRef<DashboardComponent[][]>([]);

  // Ref для актуального состояния (используется в callback-ах без зависимости)
  const componentsRef = useRef(dashboardComponents);
  componentsRef.current = dashboardComponents;

  // Ref для onRestore (стабильный доступ)
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  /**
   * Сохранить снимок текущего состояния перед мутацией.
   * Вызывать ПЕРЕД изменением dashboardComponents.
   */
  const pushSnapshot = useCallback(() => {
    const current = componentsRef.current;
    // Не пушим пустые снимки (нет блоков) — отменять нечего
    if (current.length === 0 && historyRef.current.length === 0) return;

    historyRef.current.push(current);
    // Новое действие сбрасывает redo-стек
    futureRef.current = [];

    // Ограничиваем размер истории
    if (historyRef.current.length > MAX_HISTORY_SIZE) {
      historyRef.current = historyRef.current.slice(-MAX_HISTORY_SIZE);
    }
  }, []);

  /**
   * Отменить последнее действие.
   */
  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;

    const current = componentsRef.current;
    const prev = historyRef.current.pop()!;

    // Сохраняем текущее состояние в redo-стек
    futureRef.current.push(current);

    // Восстанавливаем предыдущее состояние
    setDashboardComponents(prev);

    // Определяем, какие блоки восстановлены / удалены
    if (onRestoreRef.current) {
      const currentIds = new Set(current.map((c) => c.data?.blockId).filter(Boolean));
      const prevIds = new Set(prev.map((c) => c.data?.blockId).filter(Boolean));

      const restoredBlockIds = prev
        .map((c) => c.data?.blockId)
        .filter((id): id is string => !!id && !currentIds.has(id));
      const removedBlockIds = current
        .map((c) => c.data?.blockId)
        .filter((id): id is string => !!id && !prevIds.has(id));

      onRestoreRef.current(restoredBlockIds, removedBlockIds);
    }

    showSuccessMessage('Действие отменено (Ctrl+Z)');
  }, [ setDashboardComponents ]);

  /**
   * Повторить отменённое действие.
   */
  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;

    const current = componentsRef.current;
    const next = futureRef.current.pop()!;

    // Сохраняем текущее состояние в undo-стек
    historyRef.current.push(current);

    // Восстанавливаем следующее состояние
    setDashboardComponents(next);

    // Определяем, какие блоки восстановлены / удалены
    if (onRestoreRef.current) {
      const currentIds = new Set(current.map((c) => c.data?.blockId).filter(Boolean));
      const nextIds = new Set(next.map((c) => c.data?.blockId).filter(Boolean));

      const restoredBlockIds = next
        .map((c) => c.data?.blockId)
        .filter((id): id is string => !!id && !currentIds.has(id));
      const removedBlockIds = current
        .map((c) => c.data?.blockId)
        .filter((id): id is string => !!id && !nextIds.has(id));

      onRestoreRef.current(restoredBlockIds, removedBlockIds);
    }

    showSuccessMessage('Действие повторено (Ctrl+Shift+Z)');
  }, [ setDashboardComponents ]);

  /**
   * Обработчик клавиатуры: Ctrl+Z, Ctrl+Shift+Z, Ctrl+Y.
   */
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Игнорируем, если фокус в input/textarea/contenteditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const isMod = e.ctrlKey || e.metaKey;

      // Ctrl+Z / Cmd+Z — undo
      if (isMod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        undo();
        return;
      }

      // Ctrl+Shift+Z / Cmd+Shift+Z — redo
      if (isMod && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        redo();
        return;
      }

      // Ctrl+Y / Cmd+Y — redo (Windows)
      if (isMod && e.key === 'y') {
        e.preventDefault();
        e.stopPropagation();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [ enabled, undo, redo ]);

  return {
    pushSnapshot,
    undo,
    redo,
    canUndo: historyRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
  };
};

export default useDashboardHistory;
